/**
 * Bulletproof build-version watcher.
 *
 * Why this exists:
 *   • Cloudflare aggressively caches `index.html` at the edge for the
 *     cargodash.co.za domain. <meta http-equiv="Cache-Control"> inside
 *     the HTML doesn't influence CF — only origin response headers do —
 *     so users keep loading stale HTML even when they "hard refresh".
 *   • Inside the BlackScott Hub <iframe>, Ctrl+Shift+R on the outer page
 *     doesn't refresh the iframe URL at all, so the stale-cache problem
 *     is even more painful there.
 *   • The Service Worker shipped in /public/sw.js handles updates AFTER
 *     it's installed — but a user stuck on a pre-SW stale index.html
 *     will never install it. We need a fallback that works without the
 *     SW being active yet.
 *
 * Strategy:
 *   1. Poll `/api/version` (uncached at CF / browser) for the backend
 *      build stamp. Backend stamps a new value every container boot,
 *      which is every deploy.
 *   2. Remember the last-known stamp in `localStorage`. The first time
 *      we ever see a stamp we just remember it (no reload — we have
 *      nothing to compare against). On subsequent polls, if the stamp
 *      changed, we know the server was redeployed since this tab
 *      loaded, and the HTML in this tab is almost certainly stale.
 *   3. Force-reload the tab with `?v=<stamp>` appended to the URL.
 *      The new query string changes the CF cache key, so Cloudflare
 *      can't serve the stale `index.html` — it must go to origin,
 *      which returns the new build. Subsequent visitors get the same
 *      fresh `?v=` URL too.
 *   4. Stash the stamp we already reloaded for so we never reload
 *      twice for the same backend version (prevents accidental loops
 *      if the frontend bundle happens to be unchanged across a
 *      backend-only redeploy).
 *
 * Combined with the Service Worker, this gives belt-and-suspenders
 * cache-busting: SW handles in-session updates instantly, and this
 * watcher handles the cold-start "I opened the app and it's stale"
 * case that the SW alone can't fix.
 */

const KNOWN_KEY = "cargodash_known_version";
const RELOADED_KEY = "cargodash_reloaded_for_version";
const POLL_INTERVAL_MS = 60_000;

async function fetchVersion() {
  const url = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api/version`
    : "/api/version";
  // Append a client-side cache-buster so absolutely nothing along the
  // request path (browser HTTP cache, CDN, intermediary proxies) can
  // hand us a stale answer.
  const buster = `_=${Date.now()}`;
  const sep = url.includes("?") ? "&" : "?";
  const res = await fetch(`${url}${sep}${buster}`, {
    method: "GET",
    cache: "no-store",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`/api/version returned ${res.status}`);
  const data = await res.json();
  return data.version;
}

function safeStorage() {
  // Some browser modes (Hub iframe with strict cookie/storage policies)
  // can block localStorage. Degrade silently rather than throwing.
  try {
    const k = "__cd_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return window.localStorage;
  } catch {
    return null;
  }
}

function forceReloadFor(version) {
  const store = safeStorage();
  if (store) store.setItem(RELOADED_KEY, version);
  const url = new URL(window.location.href);
  url.searchParams.set("v", version);
  // Drop the `sso_token` from the URL on reload so we don't replay it
  // against /sso/accept (which would 401 on the second use).
  url.searchParams.delete("sso_token");
  // `replace` instead of `assign` so users can't "Back" into the stale
  // version they just escaped from.
  window.location.replace(url.toString());
}

async function checkOnce() {
  const store = safeStorage();
  let serverV;
  try {
    serverV = await fetchVersion();
  } catch {
    return; // network blip — try again next tick
  }
  if (!serverV) return;

  const known = store ? store.getItem(KNOWN_KEY) : null;
  const alreadyReloadedFor = store ? store.getItem(RELOADED_KEY) : null;

  // First-ever observation in this browser → just remember it.
  if (!known) {
    if (store) store.setItem(KNOWN_KEY, serverV);
    return;
  }

  // Server has been redeployed since the last version we knew about,
  // AND we haven't already reloaded for this exact stamp.
  if (known !== serverV && alreadyReloadedFor !== serverV) {
    if (store) store.setItem(KNOWN_KEY, serverV);
    forceReloadFor(serverV);
    return;
  }

  // Already up to date (or already reloaded for this stamp).
  if (store) store.setItem(KNOWN_KEY, serverV);
}

export function startVersionWatch() {
  // Immediate check on mount — catches users who landed on stale
  // CF-cached HTML before they touch anything.
  checkOnce();

  // Recurring poll for users who leave a tab open through a deploy.
  const interval = setInterval(checkOnce, POLL_INTERVAL_MS);

  // Also re-check whenever the tab regains focus — the most common
  // "I came back and nothing's updated" moment.
  const onVisibility = () => {
    if (document.visibilityState === "visible") checkOnce();
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
